import React from "react";
import { Award, MessageCircle } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useSettings, resolveImage, getYouTubeId } from "../lib/settings";
import { buildWhatsAppMessageUrl } from "../lib/whatsapp";
import { fireGoogleAdsConversion } from "../lib/googleAds";
import { Link } from "react-router-dom";
import { useAutoTranslate } from "../lib/autoTranslate";

const Hero = () => {
  const { t } = useI18n();
  const { settings } = useSettings();
  const s = settings || {};
  const videoId = s.hero_media_type === "video" ? getYouTubeId(s.hero_video_url) : null;
  const heroTitle = useAutoTranslate(s.hero_title || t("hero_title"));
  const heroSubtitle = useAutoTranslate(s.hero_subtitle || t("hero_sub"));

  return (
    <section id="top" className="relative min-h-[720px] text-white overflow-hidden">
      {videoId ? (
        <div className="absolute inset-0 overflow-hidden">
          <iframe
            className="absolute top-1/2 left-1/2 pointer-events-none"
            style={{
              width: "100vw",
              height: "56.25vw",
              minWidth: "177.78vh",
              minHeight: "100%",
              transform: "translate(-50%, -50%)",
            }}
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
            title="Hero background video"
            allow="autoplay; encrypted-media"
            frameBorder="0"
          />
        </div>
      ) : (
        <img
          src={resolveImage(s.hero_image) || "https://images.unsplash.com/photo-1589561454226-796a8aa89b05"}
          alt="Istanbul cityscape"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div className="absolute inset-0 hero-gradient" />

      <div className="relative container-x pt-14 pb-16 min-h-[720px] flex flex-col justify-end">
        <p className="eyebrow text-[#ffb9a8] mb-4">{s.hero_eyebrow || t("daily_departures")}</p>
        <h1 className="font-serif-display text-[44px] sm:text-[56px] md:text-[68px] leading-[1.02] max-w-[900px]">
          {heroTitle}
        </h1>
        <p className="mt-5 text-lg max-w-[560px] text-[#f6ecdf]">
          {heroSubtitle}
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/tours" className="btn-primary">
            {s.hero_cta_primary || t("choose_your_tour")}
          </Link>
          <a href="#reviews" className="btn-ghost-dark">
            {s.hero_cta_secondary || t("read_reviews")}
          </a>
          {s.show_whatsapp_hero_button !== false && s.whatsapp_number && (
            <a
              href={buildWhatsAppMessageUrl(s.whatsapp_number, s.whatsapp_hero_message)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => fireGoogleAdsConversion(s.google_ads_whatsapp_send_to)}
              className="btn-whatsapp inline-flex items-center gap-2"
            >
              <MessageCircle size={16} /> Chat on WhatsApp
            </a>
          )}
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-x-10 gap-y-6">
          <div className="flex items-center gap-5">
            {(s.hero_badges && s.hero_badges.length > 0
              ? s.hero_badges
              : [
                  { label: "Travelers' Choice", year: "2020", platform: "Tripadvisor" },
                  { label: "Certificate of Excellence", year: "2019", platform: "Tripadvisor" },
                ]
            ).map((badge, i) => (
              <div key={i} className="flex flex-col items-center gap-1.5">
                <div className="w-[72px] h-[72px] rounded-lg bg-[var(--ri-yellow)] flex flex-col items-center justify-center text-[9px] font-semibold text-[#3a2a00] leading-tight text-center px-1">
                  <Award size={18} className="mb-1" />
                  <span className="uppercase">{badge.label}</span>
                  {badge.year && <span>{badge.year}</span>}
                </div>
                {badge.platform && <span className="eyebrow text-[#cfe3f0] text-[10px]">{badge.platform}</span>}
              </div>
            ))}
          </div>
          {(s.hero_stats || []).map((st, i) => (
            <div key={i} className="max-w-[180px]">
              <div className="font-serif-display text-[22px] leading-none">{st.value}</div>
              <div className="eyebrow text-[#f0d9c9] mt-1">{st.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
